import { isVerificationStatusVerified } from '@/lib/verification/verificationStatus';
import { DOCUMENT_STATUS_FIELDS } from '@/lib/verification/documentTypeRegistry';

/**
 * The three-tier provider badge progression, derived from live verification.
 *
 * ## Why derived and never stored
 *
 * A stored tier is a snapshot of a moment. It has to be written when a document
 * clears, and it has to be walked back when one expires - and the expiry sweep
 * already downgrades individual status fields, so a stored tier would need a
 * second downgrade path that has to be remembered every time. The failure mode
 * is quiet: a provider keeps displaying "Licensed Pro" on a lapsed Red Seal
 * until someone notices and complains.
 *
 * Deriving it means there is nothing to keep in sync. It is a pure function of
 * the profile's current status fields, so the expiry sweep already downgrades
 * the tier for free, the same day, with no second code path.
 *
 * ## The tiers
 *
 *   (none)             no identity - the only state that earns nothing
 *   verified provider  identity, and a real person is known to be behind this
 *   licensed pro       a competence licence or certificate of the trade
 *   enterprise         a licence, plus the insurance that means they can carry
 *                      someone else's risk
 *
 * Each tier is strictly stronger than the one below, so the badge always
 * describes the *best* thing the provider has proved rather than the most
 * recent one they submitted.
 */

/**
 * Competence documents: proof the holder is trained and registered to do the
 * work, rather than that a business exists.
 *
 * Business existence (CIPC, sole-prop bank proof) is deliberately absent. It
 * says a company is registered, not that a person is competent, and putting it
 * here would let anyone with a company registration style themselves as a
 * licensed professional.
 */
const LICENCE_STATUS_FIELDS: readonly string[] = [
  'pirb_registration_status',
  'saqcc_gas_practitioner_status',
  'nhbrc_certificate_status',
  'red_seal_certificate_status',
  'repssa_registration_status',
  'electrical_coc_status',
  'gas_coc_status',
  'plumbing_coc_status',
  'electric_fence_coc_status',
  'water_installation_certificate_status',
  'electrical_contractor_registration_status',
  'fsp_license_status',
  'coa_certificate_status',
  'liquor_license_status',
  'ffc_certificate_status',
  'ems_fire_safety_certificate_status',
];

/**
 * Insurance. Voluntary across the platform - nothing gates on it - but it is
 * the difference between a competent individual and someone who can carry the
 * cost of damaging a customer's property, which is what Enterprise means.
 */
const ENTERPRISE_STATUS_FIELDS: readonly string[] = [
  'public_liability_insurance_status',
];

export type ProviderBadgeTier = 'none' | 'verified-provider' | 'licensed-pro' | 'enterprise';

export interface ProviderBadge {
  tier: ProviderBadgeTier;
  label: string;
  /** One line explaining what earned it. Safe to show on a profile. */
  description: string;
  /** The status field that earned the tier, for "why do I have this?" */
  earnedBy: string | null;
}

/**
 * Display maps, exported so the badge strip cannot drift from the tier logic.
 *
 * Copy lives here because a tier's label is part of what the tier *means* -
 * "Licensed Pro" and "Verified Provider" describe what was proved, and keeping
 * that wording beside the rule that grants it is the only way it stays honest.
 */
export const PROVIDER_TIER_LABEL: Record<ProviderBadgeTier, string> = {
  none: '',
  'verified-provider': 'Verified Provider',
  'licensed-pro': 'Licensed Pro',
  enterprise: 'Enterprise',
};

/**
 * Deliberately not colour-ranked.
 *
 * Enterprise is a deeper claim, not a better person, and colouring the top tier
 * gold would imply the lower ones are lesser providers. Green for the first two,
 * indigo for Enterprise, so the progression reads without a value judgement.
 */
export const PROVIDER_TIER_STYLE: Record<ProviderBadgeTier, string> = {
  none: '',
  'verified-provider': 'bg-green-600 hover:bg-green-700',
  'licensed-pro': 'bg-green-700 hover:bg-green-800',
  enterprise: 'bg-indigo-600 hover:bg-indigo-700',
};

export const PROVIDER_TIER_DESCRIPTION: Record<ProviderBadgeTier, string> = {
  none: '',
  'verified-provider': 'Identity confirmed. We know who is behind this profile.',
  'licensed-pro': 'Holds a current certificate or registration for their trade.',
  enterprise: 'Licensed for their trade and carries public liability insurance.',
};

const LABELS: Record<ProviderBadgeTier, { label: string; description: string }> = {
  none: { label: PROVIDER_TIER_LABEL.none, description: '' },
  'verified-provider': {
    label: PROVIDER_TIER_LABEL['verified-provider'],
    description: PROVIDER_TIER_DESCRIPTION['verified-provider'],
  },
  'licensed-pro': {
    label: PROVIDER_TIER_LABEL['licensed-pro'],
    description: PROVIDER_TIER_DESCRIPTION['licensed-pro'],
  },
  enterprise: {
    label: PROVIDER_TIER_LABEL.enterprise,
    description: PROVIDER_TIER_DESCRIPTION.enterprise,
  },
};

/** The first verified field in `candidates`, so the reason is the strongest one held. */
function firstVerified(
  profile: Partial<Record<string, unknown>> | null | undefined,
  candidates: readonly string[],
): string | null {
  if (!profile) return null;
  for (const field of candidates) {
    if (isVerificationStatusVerified(profile[field])) {
      return field;
    }
  }
  return null;
}

/**
 * Resolve the tier a provider currently earns.
 *
 * Identity is checked first and unconditionally: without it nothing else counts,
 * because every other document is a claim about a person we have not identified.
 */
export function resolveProviderBadgeTier(
  profile: Partial<Record<string, unknown>> | null | undefined,
): ProviderBadgeTier {
  if (!profile) return 'none';

  // HURU is read as a legacy alias for identity, for the same reason it is in
  // both payment gates: an ID submission used to mirror its result there.
  const identityVerified =
    isVerificationStatusVerified(profile.id_document_status) ||
    isVerificationStatusVerified(profile.huru_clearance_status);

  if (!identityVerified) return 'none';

  if (!firstVerified(profile, ENTERPRISE_STATUS_FIELDS)) {
    return firstVerified(profile, LICENCE_STATUS_FIELDS) ? 'licensed-pro' : 'verified-provider';
  }

  // Enterprise still requires a licence. Insurance on its own does not make
  // someone a specialist - it makes them insured to work, which is a different
  // claim, and displaying it as "Enterprise" would overstate what is proved.
  return firstVerified(profile, LICENCE_STATUS_FIELDS) ? 'enterprise' : 'verified-provider';
}

/** The badge to render, including what earned it. */
export function resolveProviderBadge(
  profile: Partial<Record<string, unknown>> | null | undefined,
): ProviderBadge {
  const tier = resolveProviderBadgeTier(profile);

  const earnedBy =
    tier === 'none'
      ? null
      : tier === 'verified-provider'
        ? isVerificationStatusVerified(profile?.huru_clearance_status) &&
          !isVerificationStatusVerified(profile?.id_document_status)
          ? 'huru_clearance_status'
          : 'id_document_status'
        : firstVerified(
            profile,
            tier === 'enterprise' ? ENTERPRISE_STATUS_FIELDS : LICENCE_STATUS_FIELDS,
          );

  return { tier, label: LABELS[tier].label, description: LABELS[tier].description, earnedBy };
}

/**
 * Every field the tiers read, for coverage tests.
 *
 * Derived from the registry rather than declared so a document type added
 * without being classified as a licence or an insurance policy fails the
 * coverage test instead of silently earning nothing.
 */
export const PROVIDER_TIER_INPUT_FIELDS: readonly string[] = DOCUMENT_STATUS_FIELDS as readonly string[];

export const LICENCE_FIELDS = LICENCE_STATUS_FIELDS;
export const ENTERPRISE_FIELDS = ENTERPRISE_STATUS_FIELDS;